<!--
  CTABandSection - Full-width CTA band with social proof

  Props:
  - backgroundColor (String): Background color of the section (default: '#1E40AF')
  - title (String): Main CTA title
  - titleHighlight (String): Highlighted portion of title
  - titleHighlightColor (String): Color of title highlight (default: 'text-blue-dark')
  - titleColor (String): Color of title text (default: 'text-white')
  - ctaText (String): CTA button text
  - ctaLink (String): CTA button link
  - showCta (Boolean): Whether to show CTA button (default: true)
  - buttonBackgroundColor (String): Background color of button (default: '#FFFFFF')
  - buttonTextColor (String): Text color of button (default: 'text-blue')
  - buttonGradientFrom (String): Start color of button gradient (optional, overrides buttonBackgroundColor)
  - buttonGradientTo (String): End color of button gradient (optional, overrides buttonBackgroundColor)
  - socialProofText (String): Text for customer count
  - socialProofImage (String): Avatar group image
  - badges (Array): Array of badge image objects
  - fallbackCtaText (String): Fallback CTA text when showCta is false
  - fallbackCtaBackgroundColor (String): Background color for fallback CTA (default: '#1E40AF')
-->
<template>
    <section class="py-20 text-white" :class="backgroundColor">
        <div class="container mx-auto px-4 text-center">
            <h3 class="text-3xl md:text-4xl font-bold mb-4" :class="titleColor">
                <span v-html="renderTitle()"></span>
            </h3>

            <router-link
                v-if="showCta"
                :to="ctaLink"
                class="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-lg transition hover:opacity-90 mb-12"
                :class="buttonTextColor"
                :style="
                    buttonGradientFrom && buttonGradientTo
                        ? { background: `linear-gradient(to right, ${buttonGradientFrom}, ${buttonGradientTo})` }
                        : { backgroundColor: buttonBackgroundColor }
                "
            >
                {{ ctaText }}
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
            </router-link>
            <div v-else class="text-white px-2 lg:px-6 py-3 rounded-full font-semibold transition text-xs lg:text-md" :style="{ backgroundColor: fallbackCtaBackgroundColor }">
                {{ fallbackCtaText }}
            </div>

            <!-- Social Proof + Awards -->
            <div v-if="socialProofText || badges.length" class="flex flex-wrap justify-center items-center gap-8">
                <div v-if="socialProofText && socialProofImage" class="flex items-center gap-3">
                    <div class="flex -space-x-2">
                        <img :src="$asset(socialProofImage)" />
                    </div>
                    <div class="text-left text-sm" v-html="socialProofText"></div>
                </div>
                <div v-for="(badge, index) in badges" :key="index" class="w-24 h-24 rounded-lg">
                    <img :alt="badge.alt" :class="badge.class || 'h-full object-cover'" :src="$asset(badge.src, 'public')" />
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
const props = defineProps({
    backgroundColor: {
        type: String,
        default: 'bg-blue-dark'
    },
    title: {
        type: String,
        default: ''
    },
    titleHighlight: {
        type: String,
        default: ''
    },
    titleHighlightColor: {
        type: String,
        default: 'text-blue-dark'
    },
    titleColor: {
        type: String,
        default: 'text-white'
    },
    ctaText: {
        type: String,
        default: ''
    },
    ctaLink: {
        type: String,
        default: '/'
    },
    showCta: {
        type: Boolean,
        default: true
    },
    buttonBackgroundColor: {
        type: String,
        default: '#FFFFFF'
    },
    buttonTextColor: {
        type: String,
        default: 'text-blue'
    },
    buttonGradientFrom: {
        type: String,
        default: ''
    },
    buttonGradientTo: {
        type: String,
        default: ''
    },
    socialProofText: {
        type: String,
        default: ''
    },
    socialProofImage: {
        type: String,
        default: ''
    },
    badges: {
        type: Array,
        default: () => []
    },
    fallbackCtaText: {
        type: String,
        default: ''
    },
    fallbackCtaBackgroundColor: {
        type: String,
        default: '#1E40AF'
    }
});

const renderTitle = () => {
    if (!props.titleHighlight) return props.title;

    const parts = props.title.split(props.titleHighlight);

    if (parts.length === 1) return props.title;

    return parts
        .map((part, index) => {
            if (index === 0) {
                return part;
            }

            return `<span class="${props.titleHighlightColor}">${props.titleHighlight}</span>${part}`;
        })
        .join('');
};
</script>
